import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config/supabaseClient";
import { FaComments, FaPaperPlane, FaTimes, FaSearch, FaBookOpen, FaQuestionCircle, FaRegFileAlt } from "react-icons/fa";
import "../assets/css/chatbot.css";

const normalize = (str) => (str || "").toLowerCase().replace(/\s+/g, " ").trim();

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      sender: "bot",
      text: "Hello! I am the Gramture Assistant. I can help you find notes, guess papers, past papers, MCQs, or grammar topics. What are you looking for today?",
      suggestions: ["Latest Materials", "MCQ Tests", "Browse Grammar"]
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [searchIndex, setSearchIndex] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);

  // Fetch search index from Supabase topics
  useEffect(() => {
    const fetchSearchIndex = async () => {
      try {
        const { data, error } = await supabase
          .from("topics")
          .select("id, topic, class, subCategory, mcqs, timestamp");
        if (error) throw error;
        
        if (data) {
          const index = data.map((doc) => {
            const hasMcqs = doc.mcqs && Array.isArray(doc.mcqs) && doc.mcqs.length > 0;
            
            // Build a clean slug
            const slug = doc.topic
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/-+/g, "-")
              .replace(/^-|-$/g, "");

            return {
              id: doc.id,
              topic: doc.topic,
              slug: slug,
              class: doc.class || "",
              subCategory: doc.subCategory || doc.class || "General",
              hasMcqs,
              timestamp: doc.timestamp ? new Date(doc.timestamp) : new Date(0)
            };
          });
          setSearchIndex(index);
        }
      } catch (err) {
        console.error("Error building search index for chatbot:", err);
      }
    };
    fetchSearchIndex();
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleSendMessage = (textToSend = "") => {
    const text = (textToSend || inputValue).trim();
    if (!text) return;

    if (!textToSend) {
      setInputValue("");
    }

    // Add user message
    const userMsgId = `user-${Date.now()}`;
    setMessages((prev) => [...prev, { id: userMsgId, sender: "user", text }]);

    setIsTyping(true);

    // Simulate bot response delay
    setTimeout(() => {
      const response = processBotQuery(text);
      setMessages((prev) => [...prev, { id: `bot-${Date.now()}`, sender: "bot", ...response }]);
      setIsTyping(false);
    }, 800);
  };

  const processBotQuery = (query) => {
    const cleanQuery = normalize(query);

    // 1. MCQ Test search
    if (cleanQuery.includes("mcq") || cleanQuery.includes("quiz") || cleanQuery.includes("test")) {
      const mcqTopics = searchIndex.filter((t) => t.hasMcqs);
      
      if (mcqTopics.length === 0) {
        return { text: "I couldn't find any MCQ Tests currently active in the database." };
      }

      // Check if specific class is mentioned, e.g. "class 9" or "9th"
      const matchClass = cleanQuery.match(/\b(9|10|11|12)\b/);
      let filtered = mcqTopics;
      let filterMsg = "";
      if (matchClass) {
        const clsNum = matchClass[1];
        filtered = mcqTopics.filter(t => normalize(t.class).includes(clsNum));
        filterMsg = ` for Class ${clsNum}`;
      }

      return {
        text: `Here are the available MCQ Tests${filterMsg}:`,
        links: filtered.slice(0, 6).map((t) => ({
          title: `${t.topic} (Class ${t.class.replace(/\D/g, "") || "X"})`,
          path: `/description/${t.subCategory}/${t.slug}`
        }))
      };
    }

    // 2. Latest material search (3 most recent posts, excluding MCQs)
    if (cleanQuery.includes("latest") || cleanQuery.includes("recent") || cleanQuery.includes("new")) {
      const nonMcqPosts = searchIndex.filter(
        (t) => !t.hasMcqs && normalize(t.subCategory) !== "mcq test" && normalize(t.topic) !== "mcq test"
      );
      
      const sorted = [...nonMcqPosts]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 3);

      if (sorted.length === 0) {
        return { text: "No study materials are available right now." };
      }

      return {
        text: "Here are the 3 most recent study materials (excluding MCQs):",
        links: sorted.map((t) => ({
          title: `${t.topic} (Class ${t.class.replace(/\D/g, "") || "X"})`,
          path: `/description/${t.subCategory}/${t.slug}`
        }))
      };
    }

    // 3. Fuzzy search for topic titles
    const queryWords = cleanQuery.split(/\s+/).filter(w => w.length > 2);
    
    let scored = searchIndex.map((t) => {
      let score = 0;
      const topicNorm = normalize(t.topic);
      const subNorm = normalize(t.subCategory);
      
      // Exact matches get massive weight
      if (topicNorm === cleanQuery) score += 100;
      else if (topicNorm.includes(cleanQuery)) score += 50;
      else if (subNorm.includes(cleanQuery)) score += 30;

      // Word matches
      queryWords.forEach((word) => {
        if (topicNorm.includes(word)) score += 10;
        if (subNorm.includes(word)) score += 5;
      });

      return { topic: t, score };
    });

    // Filter out zero scores and sort
    const matches = scored
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.topic);

    if (matches.length > 0) {
      return {
        text: "I found these relevant topics based on your search:",
        links: matches.slice(0, 5).map((t) => ({
          title: `${t.topic} (${t.subCategory})`,
          path: `/description/${t.subCategory}/${t.slug}`
        }))
      };
    }

    // 4. Default fallback
    return {
      text: "I couldn't find a direct match. Try searching for topics like 'Tenses', 'Letters', 'Applications', 'Essays', or type 'MCQ Test' to see all quizzes.",
      suggestions: ["MCQ Tests", "Latest Materials", "Browse Grammar"]
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      {!isOpen && (
        <button className="chatbot-bubble" onClick={() => setIsOpen(true)}>
          <FaComments className="chatbot-bubble-icon" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window chatbot-window-enter">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-status-indicator"></div>
              <div>
                <h4 className="chatbot-header-title">Gramture Assistant</h4>
                <p className="chatbot-header-subtitle">Online helper</p>
              </div>
            </div>
            <button className="chatbot-close-btn" onClick={() => setIsOpen(false)}>
              <FaTimes size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-message ${msg.sender}`}>
                <p style={{ margin: 0 }}>{msg.text}</p>
                
                {/* Suggestions */}
                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="chatbot-suggestions-container">
                    {msg.suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        className="chatbot-suggestion-pill"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {/* Clickable search links */}
                {msg.links && msg.links.length > 0 && (
                  <div style={{ marginTop: "10px" }}>
                    {msg.links.map((link, idx) => (
                      <Link
                        key={idx}
                        to={link.path}
                        className="chatbot-link"
                        onClick={() => setIsOpen(false)}
                      >
                        <FaBookOpen className="chatbot-link-icon" />
                        <span>{link.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="chatbot-typing-bubble">
                <div className="chatbot-dot"></div>
                <div className="chatbot-dot"></div>
                <div className="chatbot-dot"></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Panel */}
          <div className="chatbot-input-container">
            <input
              type="text"
              placeholder="Search or ask Gramture..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              className="chatbot-input"
            />
            <button className="chatbot-send-btn" onClick={() => handleSendMessage()}>
              <FaPaperPlane size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
